<template>
  <el-row type="flex" justify="center">
    <el-col :xs="24" :sm="22" :md="18" :lg="14" :xl="10">
      <FullScreenLoading v-if="loading" />
      <div v-if="!loading">
        <el-row>
          <el-col :span="24">
            <section class="breadcrumbs">
              <Paragraph
                :text="`${office} / ${event}`"
                align="center"
                :size="increaseSizeFactor"
              ></Paragraph>
              <Paragraph
                :text="`${getWeekDay()}`"
                align="center"
                :size="increaseSizeFactor"
              ></Paragraph>
              <Paragraph
                :text="`${getFormatedDate()}`"
                align="center"
                :size="increaseSizeFactor"
              ></Paragraph>
            </section>
          </el-col>
        </el-row>
        <el-row type="flex" justify="center">
          <el-col>
            <div class="search-section">
              <Heading
                :text="$t('catering.userSearchTitle')"
                :level="2"
                align="center"
                :size="increaseSizeFactor"
              ></Heading>
              <user-autocomplete
                :minlength="3"
                :users="users"
                :settings="settings"
                @confirmation="confirmPickup"
              />
            </div>
          </el-col>
        </el-row>
        <el-row type="flex" justify="center">
          <el-col>
            <div class="order-totals-section" align="center">
              <table :aria-describedby="$t(`orderList.description`)" class="orders-totals">
                <thead class="individual-list__table">
                  <tr>
                    <th scope="col">
                      {{ $t('catering.type') }}
                    </th>
                    <th scope="col">
                      {{ $t('catering.served') }}
                    </th>
                    <th scope="col">
                      {{ $t('catering.notAssisted') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(diet, key) in diets" :key="key">
                    <td class="order-name">{{ diet.name }}</td>
                    <td class="center">
                      {{ getTotalsByDiet(diet.name, true) }}
                    </td>
                    <td class="center">
                      {{ getTotalsByDiet(diet.name, false) }}
                    </td>
                  </tr>
                  <tr>
                    <td class="totals">{{ $t('catering.total') }}</td>
                    <td class="totals center">
                      {{ getTotals(true) }}
                    </td>
                    <td class="totals center">
                      {{ getTotals(false) }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </el-col>
        </el-row>
        <el-row type="flex" justify="center">
          <el-col>
            <div class="order-list-section">
              <table :aria-describedby="$t(`orderList.description`)" class="orders-by-user">
                <thead class="individual-list__table">
                  <tr>
                    <th scope="col">
                      {{ $t('catering.name') }}
                    </th>
                    <th scope="col">
                      {{ $t('catering.email') }}
                    </th>
                    <th scope="col">
                      {{ $t('catering.email') }}
                    </th>
                    <th scope="col">
                      {{ $t('catering.served') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(userOrders, key) in getFilteredUsersOrder()"
                    :key="key"
                    :class="`${userOrders.orders[event].served ? 'served' : ''}`"
                  >
                    <td class="order-name">{{ userOrders.name }}</td>
                    <td class="order-email">{{ userOrders.email }}</td>
                    <td class="order-diet">
                      {{ getDiet(userOrders.orders[event]) }}
                    </td>
                    <td>
                      <input
                        :id="userOrders.orders[event].id"
                        :checked="userOrders.orders[event].served"
                        :disabled="userOrders.orders[event].served"
                        :accesskey="userOrders.orders[event].id"
                        type="checkbox"
                        @click="markServedOrder(userOrders.id, userOrders.orders[event].id)"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-col>
  </el-row>
</template>

<script>
import UserAutocomplete from '@/components/catering/UserAutocomplete';
import FullScreenLoading from '@/components/FullScreenLoading';
import SvgIcon from '@/components/SvgIcon';
import Heading from '@/components/Heading';
import Paragraph from '@/components/Paragraph';
import { ROUTES, GRILL_DIET_KEY } from '@/constants';
import { mapActions, mapState } from 'vuex';
import moment from 'moment';

/**
 * Settings
 * @typedef {Object} CateringSettings
 * @property {string} error - If error exists in the stored settings.
 * @property {Object} settings - Object containg all possible catering settings.
 */

/**
 * Catering Data
 * @typedef {Object} CateringData
 * @property {Object[]} users - Array of users with his/her orders for the day.
 * @property {Object[]} mealsInfo - Array of the meals served that day.
 * @property {string} date - Date of the data.
 */
/**
 * Catering State
 * @typedef {Object} CateringState
 * @property {string} pinCode - PinCode retrieved from a remote endpoint.
 * @property {CateringSettings} settings - Settings
 * @property {CateringData} data - Catering Data fetched from remote server or local storage.
 */

export default {
  name: 'CateringPage',
  components: {
    UserAutocomplete,
    FullScreenLoading,
    SvgIcon,
    Heading,
    Paragraph,
  },
  async created() {
    await this.getSettings();
  },
  data() {
    return {
      loading: true,
      increaseSizeFactor: 2,
    };
  },
  methods: {
    ...mapActions('catering', {
      getSettings: 'getCateringSettings',
      getData: 'getRemoteCateringData',
      checkLocalUserOrder: 'checkLocalUserOrder',
    }),
    ...mapActions('orders', {
      checkUserOrder: 'checkOutUserOrder',
    }),
    async confirmPickup(orderObject) {
      const { userId, orderId } = orderObject;
      const orderProps = { orderId, served: true };
      await this.checkLocalUserOrder({ userId, ...orderProps }); // trigger action to save it locally
      await this.checkUserOrder(orderProps); // trigger action to save it in the remote DB
    },
    getDiet(userMeal) {
      const selectedMeal = this.mealsInfo.find((x) => x.id === userMeal.meal);
      const dietName = selectedMeal ? selectedMeal.dietInfo.name : '';
      if (!dietName) return dietName;
      return dietName.toLowerCase() === GRILL_DIET_KEY
        ? `${this.$t('orderReport.grill')} - ${selectedMeal.name}`
        : dietName;
    },
    getDietKey(userMeal) {
      const selectedMeal = this.mealsInfo.find((x) => x.id === userMeal.meal);
      return selectedMeal ? selectedMeal.dietInfo.name : '';
    },
    markServedOrder(userId, orderId) {
      this.confirmPickup({ userId, orderId });
    },
    getWeekDay() {
      return moment(this.date).format('dddd');
    },
    getFormatedDate() {
      return moment(this.date).format('MMM Do, YY');
    },
    getTotalsByDiet(diet, served) {
      var dietOrders = 0;
      this.users
        .filter((userOrder) => {
          return userOrder.orders[this.event] && userOrder.orders[this.event].served === served;
        })
        .map((userOrder) => {
          const userOrderDiet = this.getDietKey(userOrder.orders[this.event]);
          if (userOrderDiet === diet) {
            dietOrders++;
          }
        });
      return dietOrders;
    },
    getTotals(served) {
      const filteredByStatus = this.users.filter((userOrder) => {
        return (
          userOrder.orders[this.event] &&
          userOrder.orders[this.event].served === served &&
          this.getDiet(userOrder.orders[this.event])
        );
      });
      return filteredByStatus.length;
    },
    getFilteredUsersOrder() {
      const filteredByOrderExists = this.users.filter((userOrder) => {
        return (
          userOrder.orders[this.event] != undefined && this.getDiet(userOrder.orders[this.event])
        );
      });
      return filteredByOrderExists;
    },
  },
  computed: {
    ...mapState('catering', {
      settings: ({ catering }) => catering.settings,
      users: ({ catering }) => catering.data.users,
      office: ({ catering }) => catering.settings.settings.office.name,
      event: ({ catering }) => catering.settings.settings.event.name,
      date: ({ catering }) => catering.settings.settings.date,
      diets: ({ catering }) => catering.settings.settings.diets,
      mealsInfo: (state) => state.catering.data.mealsInfo,
    }),
  },
  watch: {
    async settings() {
      const { error, settings } = this.settings;
      const toCateringRoute = error || !settings;
      if (toCateringRoute) {
        this.$router.push(ROUTES.CATERING_SETTINGS);
        return;
      } else {
        const { date, office } = settings;
        const formattedDate = moment(date).format('YYYY-MM-DD');
        this.loading = true;
        await this.getData({ date: formattedDate, office: office.id });
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../scss/variables';
.search-section {
  min-height: 120px;
}
/deep/ .el-autocomplete {
  width: 100%;
}
.or {
  padding-bottom: 20px;
}
.qr-label {
  padding: 40px 0;
}
.qr-container {
  padding: 20px 0 40px;
  text-align: center;
}
.qr-icon {
  cursor: pointer;
  width: 170px;
}
.el-alert--error {
  margin-bottom: 0;
}
.breadcrumbs {
  margin-bottom: 16px;
}
.orders-by-user {
  width: 100%;
  margin-top: 2em;
  .order-name {
    width: 55%;
  }
  .order-diet {
    width: 15%;
  }
  td {
    border: 1px solid $orders-by-user-border;
    padding: 0.3em 0.6em;
  }
  tr:nth-child(even) {
    background-color: #f2f2f2;
  }
  tr.served {
    color: #828a8f;
  }
  th {
    border: 1px solid $orders-by-user-border;
    padding: 0.4em 0.6em;
  }
}
.orders-totals {
  width: 50%;
  margin-top: 2em;
  td {
    border: 1px solid $orders-by-user-border;
    padding: 0.3em 0.6em;
    width: 33%;
  }
  th {
    border: 1px solid $orders-by-user-border;
    background-color: #284f87;
    color: #fff;
    padding: 0.4em 0.6em;
  }
  td.totals {
    background-color: #aec5e6;
  }
  td.center {
    text-align: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/Catering/CateringPage.vue